/**
 * EWSRiskCard - Risk summary card (High Risk / Critical)
 *
 * Large cards displaying risk category counts with percentage and navigation arrow.
 * Used in the Overview tab for High Risk and Critical customer counts.
 */

import React, { useState } from 'react';
import { ChevronRight, Info, TrendingUp } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { EWSRiskCardProps, formatIndianNumber } from '../../../types/ews.types';

export const EWSRiskCard: React.FC<EWSRiskCardProps> = ({
  title,
  count,
  percentage,
  variant,
  onClick,
  infoTooltip,
}) => {
  // Tooltip state for hybrid behavior (hover on desktop, tap on mobile)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  // Colors based on variant
  const isHigh = variant === 'high';
  const textColor = isHigh ? 'text-red-600' : 'text-red-700';

  return (
    <div
      className="relative rounded-2xl cursor-pointer hover:shadow-lg transition-all overflow-hidden"
      style={{
        background: isHigh
          ? 'linear-gradient(135deg, rgba(254, 226, 226, 0.8) 0%, rgba(255, 255, 255, 1) 100%)'
          : 'linear-gradient(135deg, rgba(254, 243, 199, 0.6) 0%, rgba(255, 255, 255, 1) 100%)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
        border: '1px solid rgba(0, 0, 0, 0.04)',
      }}
      onClick={onClick}
    >
      <div className="px-4 py-4">
        {/* Header with title and info icon */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">
            {title}
          </span>
          {infoTooltip && (
            <Tooltip.Provider delayDuration={200}>
              <Tooltip.Root
                open={isTooltipOpen}
                onOpenChange={setIsTooltipOpen}
              >
                <Tooltip.Trigger asChild>
                  <button
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Toggle on tap for mobile devices
                      setIsTooltipOpen((prev) => !prev);
                    }}
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className="max-w-xs px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg z-50"
                    sideOffset={5}
                    onPointerDownOutside={() => setIsTooltipOpen(false)}
                  >
                    {infoTooltip}
                    <Tooltip.Arrow className="fill-gray-900" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          )}
        </div>

        {/* Main content */}
        <div className="flex items-center justify-between">
          {/* Count and percentage */}
          <div className="flex items-baseline gap-3">
            <span className={`text-3xl font-bold ${textColor}`}>
              {formatIndianNumber(count)}
            </span>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" style={{ color: '#F87171' }} />
              <span className="text-sm" style={{ color: '#F87171' }}>
                {percentage}% of total
              </span>
            </div>
          </div>

          {/* Navigation arrow */}
          <button
            className="flex items-center justify-center w-8 h-8 rounded-full bg-white hover:bg-gray-50 transition-colors"
            style={{
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
            }}
            onClick={onClick}
          >
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EWSRiskCard;
